import { useState, useEffect } from "react";
import { Search, TrendingUp, TrendingDown, Zap, Bitcoin, RefreshCw, ChevronDown, Clock, Activity, Filter } from "lucide-react";
import { API_BASE } from "@/lib/api";

interface Market {
  id: string;
  question: string;
  category: string;
  tags: string[];
  volume: number;
  volume24hr?: number;
  liquidity: number;
  yes_price: number;
  no_price: number;
  image_url?: string;
  icon?: string;
  end_date: string;
}

const ALL_CATEGORIES = [
  "Trending", "Breaking", "New", 
  "Politics", "Sports", "Crypto", "Esports", 
  "Iran", "Finance", "Geopolitics", "Tech", 
  "Culture", "Economy", "Weather", "Mentions", "Elections",
  "Crypto 5m", "Crypto 15m", "Crypto 1h"
];

const TIME_FILTERS = ["5 Min", "15 Min", "1 Hour", "4 Hours", "Daily", "Weekly"];

export default function MarketsPage() {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeTimeFilter, setActiveTimeFilter] = useState<string | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);

  const fetchAllMarkets = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/markets/live`);
      const data = await r.json();
      if (data.markets_by_category) {
        const all = Object.values(data.markets_by_category).flat() as Market[];
        // Deduplicate — markets can appear in parent + subcategory groups
        const seen = new Set<string>();
        const unique = all.filter(m => {
          if (seen.has(m.id)) return false;
          seen.add(m.id);
          return true;
        });
        setMarkets(unique);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  // Helper: check if market belongs to a category (including subcategories)
  const marketInCategory = (m: Market, cat: string): boolean => {
    if (m.category === cat) return true;
    if (m.category?.startsWith(cat + " ")) return true;
    return m.tags?.some(t => t.toLowerCase() === cat.toLowerCase()) ?? false;
  };

  useEffect(() => {
    fetchAllMarkets();
    const interval = setInterval(fetchAllMarkets, 15000);
    return () => clearInterval(interval);
  }, []);

  // Filter markets
  let filtered = markets;
  
  if (search) {
    filtered = filtered.filter(m => 
      m.question.toLowerCase().includes(search.toLowerCase())
    );
  }
  
  if (activeCategory !== "All") {
    if (activeCategory === "Trending") {
      filtered = filtered.sort((a, b) => (b.volume24hr || b.volume) - (a.volume24hr || a.volume)).slice(0, 50);
    } else if (activeCategory === "New") {
      filtered = filtered.filter(m => {
        const days = (Date.now() - new Date(m.end_date).getTime()) / (1000 * 60 * 60 * 24);
        return days > -30; // Recent
      });
    } else if (activeCategory === "Breaking") {
      // High volume + recent
      filtered = filtered.filter(m => m.volume > 1000000).sort((a, b) => b.volume - a.volume);
    } else {
      filtered = filtered.filter(m => marketInCategory(m, activeCategory));
    }
  }

  // Crypto time filter
  if (activeTimeFilter) {
    const timeMap: Record<string, string[]> = {
      "5 Min": ["5m", "5 min"],
      "15 Min": ["15m", "15 min"], 
      "1 Hour": ["1h", "1 hour"],
      "4 Hours": ["4h", "4 hour"],
      "Daily": ["daily", "1d"],
      "Weekly": ["weekly", "1w"]
    };
    const terms = timeMap[activeTimeFilter];
    filtered = filtered.filter(m => {
      const q = m.question.toLowerCase();
      return terms.some(t => q.includes(t));
    });
  }

  if (selectedMarket) {
    return <MarketDetail market={selectedMarket} onBack={() => setSelectedMarket(null)} />;
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity size={22} className="text-blue-400" />
            ALL POLYMARKET MARKETS
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            {markets.length.toLocaleString()} markets loaded | Live updates every 15s
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchAllMarkets} className="terminal-btn text-xs flex items-center gap-1 border-blue-500/50 text-blue-400">
            <RefreshCw size={12} /> REFRESH
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search polymarkets..."
          className="terminal-input w-full text-xs pl-9"
        />
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
        <button
          onClick={() => { setActiveCategory("All"); setActiveTimeFilter(null); }}
          className={`px-3 py-1.5 text-xs rounded whitespace-nowrap ${
            activeCategory === "All" 
              ? "bg-blue-500/20 text-blue-400 border border-blue-500/50" 
              : "text-gray-400 hover:text-white"
          }`}
        >
          All
        </button>
        {ALL_CATEGORIES.map(cat => {
          const count = markets.filter(m => marketInCategory(m, cat)).length;
          return (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setActiveTimeFilter(null); }}
              className={`px-3 py-1.5 text-xs rounded whitespace-nowrap ${
                activeCategory === cat 
                  ? "bg-blue-500/20 text-blue-400 border border-blue-500/50" 
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {cat} {count > 0 && <span className="text-gray-600">({count})</span>}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-4 gap-4">
        {/* Left: Time Filters (Crypto only) */}
        {activeCategory === "Crypto" && (
          <div className="col-span-1 space-y-1">
            <div className="text-xs text-gray-500 mb-2 font-bold">TIME FRAME</div>
            {TIME_FILTERS.map(tf => {
              const count = markets.filter(m => {
                const q = m.question.toLowerCase();
                const map: Record<string, string[]> = {
                  "5 Min": ["5m", "5 min"], "15 Min": ["15m", "15 min"],
                  "1 Hour": ["1h", "1 hour"], "4 Hours": ["4h", "4 hour"],
                  "Daily": ["daily", "1d"], "Weekly": ["weekly", "1w"]
                };
                return map[tf]?.some(t => q.includes(t));
              }).length;
              
              return (
                <button
                  key={tf}
                  onClick={() => setActiveTimeFilter(activeTimeFilter === tf ? null : tf)}
                  className={`w-full flex justify-between items-center px-3 py-2 text-xs rounded ${
                    activeTimeFilter === tf 
                      ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" 
                      : "text-gray-400 hover:bg-white/5"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Clock size={12} /> {tf}
                  </span>
                  <span className="text-gray-600">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Right: Market Grid */}
        <div className={`${activeCategory === "Crypto" ? "col-span-3" : "col-span-4"}`}>
          {loading && filtered.length === 0 ? (
            <div className="text-center text-gray-500 py-20">Loading markets...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-500 py-20">No markets found</div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {filtered.map(m => (
                <div
                  key={m.id}
                  onClick={() => setSelectedMarket(m)}
                  className="terminal-window p-3 cursor-pointer hover:border-blue-500/50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    {/* Image/Icon */}
                    {m.image_url || m.icon ? (
                      <img 
                        src={m.image_url || m.icon} 
                        alt="" 
                        className="w-10 h-10 rounded-lg object-cover shrink-0"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        {m.category === "Crypto" ? <Bitcoin size={20} className="text-orange-400"/> : <Zap size={20} className="text-blue-400"/>}
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium leading-tight truncate group-hover:text-blue-400 transition-colors">
                        {m.question}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-gray-500">Vol: ${m.volume?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Price row */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs">
                      <span className="text-blue-400 font-bold">YES {(m.yes_price * 100).toFixed(0)}%</span>
                      <span className="text-gray-600 mx-1">|</span>
                      <span className="text-red-400">NO {(m.no_price * 100).toFixed(0)}%</span>
                    </div>
                    <div className="flex gap-1">
                      <button className="px-2 py-1 text-[10px] bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30">
                        Yes
                      </button>
                      <button className="px-2 py-1 text-[10px] bg-red-500/20 text-red-400 rounded hover:bg-red-500/30">
                        No
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual Market Detail Page
function MarketDetail({ market, onBack }: { market: Market; onBack: () => void }) {
  const [timeFrame, setTimeFrame] = useState("5 Min");
  const [orderType, setOrderType] = useState<"buy" | "sell">("buy");
  const [side, setSide] = useState<"yes" | "no">("yes");
  const [amount, setAmount] = useState(10);

  const avgPrice = (market.yes_price + market.no_price) / 2;
  const potentialWin = side === "yes" 
    ? amount / market.yes_price * (1 - market.yes_price)
    : amount / market.no_price * (1 - market.no_price);

  return (
    <div className="p-4 space-y-4">
      {/* Back button */}
      <button onClick={onBack} className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
        <ChevronDown size={12} className="rotate-90" /> Back to markets
      </button>

      <div className="grid grid-cols-3 gap-4">
        {/* Chart Area */}
        <div className="col-span-2 space-y-4">
          {/* Market Header */}
          <div className="flex items-start gap-4">
            {market.image_url && (
              <img src={market.image_url} alt="" className="w-14 h-14 rounded-xl object-cover" />
            )}
            <div>
              <h1 className="text-lg font-bold text-white">{market.question}</h1>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Activity size={10} className="text-green-400" /> LIVE
                </span>
                <span>Vol: ${market.volume?.toLocaleString()}</span>
                <span>Liq: ${market.liquidity?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Prices */}
          <div className="flex gap-4">
            <div className="flex-1 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
              <div className="text-xs text-gray-500">YES Price</div>
              <div className="text-2xl font-bold text-blue-400">{(market.yes_price * 100).toFixed(1)}¢</div>
            </div>
            <div className="flex-1 p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
              <div className="text-xs text-gray-500">NO Price</div>
              <div className="text-2xl font-bold text-red-400">{(market.no_price * 100).toFixed(1)}¢</div>
            </div>
          </div>

          {/* Chart Placeholder */}
          <div className="terminal-window h-64 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-gray-500 text-sm">
                <TrendingUp size={48} className="mx-auto mb-2 text-blue-400/30" />
                Price Chart<br/>
                <span className="text-xs">Will display trade history & price movement</span>
              </div>
            </div>
            {/* Simulated line */}
            <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              <path
                d={`M0,${200 - market.yes_price * 200} ${Array.from({length: 20}, (_, i) => 
                  `L${(i+1) * 5}%,${200 - (market.yes_price * 200 + Math.sin(i) * 20)}`
                ).join(' ')}`}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                opacity="0.5"
              />
            </svg>
          </div>

          {/* Order Book */}
          <div className="terminal-window">
            <div className="terminal-header text-xs">ORDER BOOK</div>
            <div className="p-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] text-blue-400 mb-1">YES ORDERS</div>
                  <div className="space-y-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-blue-400">{(market.yes_price * 100 + i * 2).toFixed(1)}¢</span>
                        <span className="text-gray-500">${((5-i) * 500).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-red-400 mb-1">NO ORDERS</div>
                  <div className="space-y-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-red-400">{(market.no_price * 100 - i * 2).toFixed(1)}¢</span>
                        <span className="text-gray-500">${((5-i) * 500).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trading Panel */}
        <div className="space-y-3">
          {/* Buy/Sell Toggle */}
          <div className="flex">
            <button 
              onClick={() => setOrderType("buy")}
              className={`flex-1 py-2 text-xs font-bold ${orderType === "buy" ? "bg-blue-500/20 text-blue-400 border-b-2 border-blue-500" : "text-gray-500"}`}
            >
              Buy
            </button>
            <button 
              onClick={() => setOrderType("sell")}
              className={`flex-1 py-2 text-xs font-bold ${orderType === "sell" ? "bg-red-500/20 text-red-400 border-b-2 border-red-500" : "text-gray-500"}`}
            >
              Sell
            </button>
          </div>

          {/* Side Selection */}
          <div className="flex gap-2">
            <button
              onClick={() => setSide("yes")}
              className={`flex-1 py-3 rounded-lg text-sm font-bold ${
                side === "yes" 
                  ? "bg-blue-500 text-white" 
                  : "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
              }`}
            >
              <TrendingUp size={14} className="inline mr-1" />
              Up {(market.yes_price * 100).toFixed(0)}¢
            </button>
            <button
              onClick={() => setSide("no")}
              className={`flex-1 py-3 rounded-lg text-sm font-bold ${
                side === "no" 
                  ? "bg-red-500 text-white" 
                  : "bg-red-500/10 text-red-400 hover:bg-red-500/20"
              }`}
            >
              <TrendingDown size={14} className="inline mr-1" />
              Down {(market.no_price * 100).toFixed(0)}¢
            </button>
          </div>

          {/* Amount */}
          <div>
            <label className="text-xs text-gray-500">Amount (USD)</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value))}
              className="terminal-input w-full mt-1 text-sm"
            />
          </div>

          {/* Quick amounts */}
          <div className="flex gap-2">
            {[5, 25, 100].map(amt => (
              <button
                key={amt}
                onClick={() => setAmount(amt)}
                className="flex-1 py-2 text-xs bg-white/5 rounded hover:bg-white/10 text-gray-400"
              >
                <div className="font-bold">${amt}</div>
                <div className="text-[10px] text-gray-600">win ${(amt / (side === "yes" ? market.yes_price : market.no_price) * (1 - (side === "yes" ? market.yes_price : market.no_price))).toFixed(0)}</div>
              </button>
            ))}
          </div>

          {/* Summary */}
          <div className="p-3 bg-white/5 rounded-lg space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Average Price</span>
              <span className="text-white">{(avgPrice * 100).toFixed(1)}¢</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Shares</span>
              <span className="text-white">{(amount / avgPrice).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Potential Win</span>
              <span className="text-green-400">+${potentialWin.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Max Loss</span>
              <span className="text-red-400">-${amount.toFixed(2)}</span>
            </div>
          </div>

          {/* Submit */}
          <button className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg text-sm">
            {orderType === "buy" ? "Buy" : "Sell"} {side.toUpperCase()}
          </button>

          <p className="text-[10px] text-gray-600 text-center">
            By trading, you agree to the Terms of Use
          </p>
        </div>
      </div>
    </div>
  );
}
